import type { PaywallTrigger } from "@/lib/analytics/paywall";
import { quotaLimitToTrigger } from "@/lib/analytics/paywall";
import type { QuotaLimit } from "./quota";

export interface PaywallCopy {
  heading: string;
  body?: string;
  benefit: string;
}

const DEFAULT_COPY: PaywallCopy = {
  heading: "Unlock this feature",
  body: "This feature isn’t included on your current plan.",
  benefit: "Get the paid mapping tools you need as your work grows.",
};

/**
 * One copy catalog for both quota-backed paywalls and binary feature paywalls.
 * A trigger has the same heading, explanation, and benefit everywhere it opens.
 */
export function paywallCopyForTrigger(trigger: PaywallTrigger | undefined, brandName: string): PaywallCopy {
  switch (trigger) {
    case "private_share":
      return {
        heading: "Publish an unlisted map",
        body: "Unlisted publishing isn’t included on your current plan.",
        benefit: "Share a map by link without listing it publicly.",
      };
    case "share_password":
      return {
        heading: "Password Protect Your Maps",
        body: "Password-protected sharing isn’t included on your current plan.",
        benefit: "Share maps securely with only the people you choose.",
      };
    case "data_export":
      return {
        heading: "Export your map data",
        body: "Data exports aren’t included on your current plan.",
        benefit: "Download your work as CSV, KML, or GeoJSON.",
      };
    case "api_access":
      return {
        heading: "Get map API access",
        body: "API access isn’t included on your current plan.",
        benefit: "Create API keys and use your maps and datasets programmatically.",
      };
    case "organize":
      return {
        heading: "Organize your maps",
        body: "Folders and tags aren’t included on your current plan.",
        benefit: "Sort maps into folders and add tags that fit your workflow.",
      };
    case "remove_branding":
      return {
        heading: `Remove ${brandName} branding`,
        body: "Your current plan includes attribution on published embeds.",
        benefit: "Publish clean, badge-free embeds under your own brand.",
      };
    case "refresh_quota_hit":
      return {
        heading: "Keep your maps up to date",
        body: "Scheduled refresh isn’t included on your current plan.",
        benefit: "Refresh maps automatically on a schedule.",
      };
    case "live_embed":
      return {
        heading: "Keep this map connected",
        body: "Live maps and connected sources aren’t included on your current plan.",
        benefit: "Keep maps synchronized with the data behind them.",
      };
    case "satellite_basemap":
      return {
        heading: "Publish with satellite imagery",
        body: "You can save this map as a draft, but publishing with the satellite basemap requires Lite.",
        benefit: "Publish maps with satellite imagery.",
      };
    case "save_quota_hit":
      return {
        heading: "Save this map and keep editing",
        body: "You’ve reached the saved map limit on your current plan.",
        benefit: "Save more maps without losing your work",
      };
    case "map_points":
      return {
        heading: "Unlock maps with more locations.",
        body: "Your map has more locations than your current plan allows.",
        benefit: "Create maps without a per-map location cap.",
      };
    case "nl_in_embed":
      return {
        heading: "Add natural-language tools to this map",
        body: "Embedded natural-language tools aren’t included on your current plan.",
        benefit: "Let viewers explore map data using everyday language.",
      };
    case "ask_map":
      return {
        heading: "Ask your map with Pro",
        body: "Use plain language to explore and update your map.",
        benefit: "Ask questions and make map changes without manual setup.",
      };
    case "paid_geocode_fallback":
      return {
        heading: "Finish locating every row",
        body: "Some locations need paid geocoding to resolve.",
        benefit: "Resolve more addresses and finish building this map.",
      };
    default:
      return DEFAULT_COPY;
  }
}

export function paywallCopyForQuotaLimit(limit: QuotaLimit, brandName: string): PaywallCopy {
  return paywallCopyForTrigger(quotaLimitToTrigger(limit), brandName);
}
